import { apiDelete, apiGet, apiPost, apiPut, apiPutParamsOnly, validateRequiredParams } from '@/services/BaseApiService';

const conversationBasePath = '/conversations';

export const getConversations = async(beginDate, endDate, openOnly) => {
  const formatDate = (date) => {
    if (!date) return null;
    if (typeof date === 'string') {
      // If it's already a string, extract just the date part
      return date.split('T')[0];
    }
    if (date instanceof Date) {
      // If it's a Date object, convert to ISO and extract date part
      return date.toISOString().split('T')[0];
    }
    return null;
  };
  openOnly = openOnly || false;

  const formattedBeginDate = formatDate(beginDate);
  const formattedEndDate = formatDate(endDate);

  return await apiGet(`${conversationBasePath}/all`, { openOnly, beginDate: formattedBeginDate, endDate: formattedEndDate });
};

export const getDashboardConversations = async(organizationId, openOnly = true) => {
  validateRequiredParams({ organizationId });
  return await apiGet(`${conversationBasePath}/dashboard`, { organizationId, openOnly });
};

export const getAvailableConversationOrganizations = async(includeAll, organizationTypeId) => {
  return await apiGet(`${conversationBasePath}/availableorganizations`, { includeAll, organizationTypeId });
};

export const getAvailableConversationContacts = async(organizationId, organizationTypeId) => {
  validateRequiredParams({ organizationId });
  const typeId = organizationTypeId || null;
  return await apiGet(`${conversationBasePath}/availablecontacts`, { organizationId, organizationTypeId: typeId });
};

export const insertConversation = async(conversationData) => {
  validateRequiredParams({ conversationData });
  return await apiPost(`${conversationBasePath}`, conversationData);
};

export const insertGeneralNotice = async(generalNoticeData) => {
  validateRequiredParams({ generalNoticeData });
  return await apiPost(`${conversationBasePath}/generalnotice`, generalNoticeData);
};

export const getConversation = async(conversationId, threadId) => {
  validateRequiredParams({ conversationId });
  return await apiGet(`${conversationBasePath}/${conversationId}`, { threadId: threadId || null });
};

export const getConversationContacts = async(conversationId, threadId) => {
  validateRequiredParams({ conversationId });
  return await apiGet(`${conversationBasePath}/${conversationId}/contacts`, { threadId: threadId || null });
};

export const getConversationMessages = async(conversationId, threadId) => {
  validateRequiredParams({ conversationId });
  return await apiGet(`${conversationBasePath}/${conversationId}/messages`, { threadId: threadId || null });
};

export const insertConversationMessage = async(conversationId, messageData) => {
  validateRequiredParams({ conversationId, messageData });
  return await apiPost(`${conversationBasePath}/${conversationId}/messages`, messageData);
};

export const getAvailableComponentLookups = async(organizationId) => {
  validateRequiredParams({ organizationId });
  return await apiGet(`${conversationBasePath}/components/available`, { organizationId });
};

export const getConversationComponents = async(reportId, organizationId, sectionId) => {
  validateRequiredParams({ reportId, organizationId });
  return await apiGet(`${conversationBasePath}/components`, { reportId, organizationId, sectionId });
};

export const insertConversationComponent = async(componentData) => {
  validateRequiredParams({ componentData });
  return await apiPost(`${conversationBasePath}/components`, componentData);
};

export const updateConversationComponent = async(componentId, componentData) => {
  validateRequiredParams({ componentId, componentData });
  return await apiPut(`${conversationBasePath}/components`, componentData);
};

export const setConversationStatus = async(conversationId, isClosed) => {
  validateRequiredParams({ conversationId, isClosed });
  return await apiPutParamsOnly(`${conversationBasePath}/${conversationId}/status`, { isClosed });
};

export const deleteConversationComponent = async(componentId) => {
  validateRequiredParams({ componentId });
  return await apiDelete(`${conversationBasePath}/components/${componentId}`);
};

export const markConversationAsRead = async(conversationId) => {
  validateRequiredParams({ conversationId });
  return await apiPut(`${conversationBasePath}/${conversationId}/markread`);
};